import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import {
  ControlContainer,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormControlDirective,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms'
import {BehaviorSubject, debounceTime, distinctUntilChanged, of, skip, Subject, switchMap, takeUntil,} from "rxjs";
import {filter, map} from "rxjs/operators";
import {Backend,} from "@generated-backend-types";
import {isArray} from "ngx-bootstrap/chronos";
import {sortListByGivenKey} from "../../../utils/sort-list-by-given-key";
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {NgMultiSelectDropDownModule} from "ng-multiselect-dropdown";
import {BaseEndpointService} from '../../../services/api/base-endpoint-service';
import {ListItem} from '../../../entities/list-item.entity';
import UuidAttributeData = Backend.App.Data.Meta.UuidAttributeData;
import {KeyValuePipe, NgForOf, NgIf} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';


@Component({
  selector: 'app-multi-select-with-service-list[service][searchOnField]',
  templateUrl: './multi-select-with-service-list.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: MultiSelectWithServiceListComponent,
    multi: true
  }],
  imports: [TooltipModule, NgMultiSelectDropDownModule, FormsModule, NgIf, TranslatePipe, NgForOf, KeyValuePipe],
  standalone: true
})
export class MultiSelectWithServiceListComponent<Model extends BaseResource, Data = Model> implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() public label!: string
  @Input() public inline = false

  @Input() public small = false
  @Input() public searchOnField?: string;
  @Input() public errorMessages!: Record<string, string>
  @Input() public name!: string
  @Input() public prefix?: string
  @Input() public service!: BaseEndpointService<Model, Data>
  @Input() public selectionMode: 'single_id' | 'multiple_ids' = 'multiple_ids'
  @Input() public sort = '-state'
  /** Disables sort that's being requested by the frontend */
  @Input() public backendSort: boolean = false;
  @Input() public tooltipText?: string
  @Input() public filters?: {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }
  @Input() public reloadList$?: Subject<void>;
  @Input() public preloadedSelectedModels: null | undefined | Model | Model[];
  @Output() public selectedFullItems: EventEmitter<null | string | string[] | ListItem | ListItem[]> = new EventEmitter<null | string | string[] | ListItem | ListItem[]>();
  @Output() public selectedFullModels: EventEmitter<null | string | Model | Model[]> = new EventEmitter<null | string | Model | Model[]>();
  @Input() public skipInitLoading: boolean = false;
  @Input() public disableLocalCache: boolean = false;
  @Input() public showSelectAll: boolean = false;
  public selectedItems: ListItem[] = []
  public readonly Infinity = Infinity;
  /**
   * @see https://github.com/nileshpatel17/ng-multiselect-dropdown#settings
   */
  public multiSelectSearchFilterChanged: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined)
  public multiSelectIsLoading = false;
  public allSelectOptions: (ListItem & { model: Model })[] = [];
  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;
  @Input()
  public formControl?: FormControl<Model[] | string | null | undefined | string[]> | FormControl<UuidAttributeData[]>
  @Input()
  public formControlName!: string;
  @Input()
  public closeDropDownOnSelection = false;
  private onDestroy$: Subject<void> = new Subject<void>()

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector,
    // private readonly localeService: LocaleService,
    private fb: FormBuilder,
  ) {}

  @Input()
  public set reloadEmitter(e: EventEmitter<void>) {
    e.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(() => {
      this.reload();
    })
  }

  public get control(): FormControl<null | undefined | string | string[] | ListItem | ListItem[]> {

    if (this.formControl) {
      return <FormControl<null | undefined | string | string[] | ListItem | ListItem[]>>this.formControl
    }
    const controlCont = this.controlContainer
    if (this.formControlName && controlCont?.control !== null) {
      return controlCont?.control.get(this.formControlName) as FormControl<null | undefined | string | string[] | ListItem | ListItem[]>;
    }
    return new FormControl<null | undefined | string | string[] | ListItem | ListItem[]>(null)

  }

  get controlContainer(): ControlContainer | null {
    return this.injector.get(ControlContainer, null);
  }

  public selectionChanged() {
    this.control?.markAsTouched()

    switch (this.selectionMode) {
      case "single_id": {
        const id = this.selectedItems.length === 1 ? (this.selectedItems[0].id.toString()) : null;
        const fullItem = this.allSelectOptions.find(option => option.id === id);
        this.selectedFullModels.emit(fullItem?.model ?? null);
        this.selectedFullItems.emit(id);
        this.control?.setValue(id)
        return;
      }
      default: {
        const ids = this.selectedItems.map(item => item.id.toString());
        const fullItems = this.allSelectOptions.filter(option => ids.includes(option.id.toString()));
        this.selectedFullModels.emit(fullItems.map(item => item.model) ?? []);
        this.selectedFullItems.emit(ids);
        this.control?.setValue(ids);
        return;
      }
    }
  }

  public selectAll(): void {
    this.control?.markAsTouched()

    switch (this.selectionMode) {
      case "multiple_ids": {
        const ids = this.allSelectOptions.map(item => item.id.toString());
        this.selectedFullItems.emit(ids);
        this.control?.setValue(ids);
        return;
      }
      default:
        this.selectedFullItems.emit(this.allSelectOptions);
        this.control?.setValue(this.allSelectOptions);
    }
  }

  public deselectAll(): void {
    this.control?.markAsTouched()

    this.selectedFullModels.emit([]);
    this.selectedFullItems.emit([]);
    this.control?.setValue([]);
  }

  public ngOnInit() {
    this.multiSelectSearchFilterChanged?.next('');
    if (this.preloadedSelectedModels) this.updateSelectedItemsForControlChange(this.preloadedSelectedModels)
    this.control?.valueChanges.subscribe(
      v => this.updateSelectedItemsForControlChange(v)
    )

    this.multiSelectSearchFilterChanged.pipe(
      filter(d => d !== undefined),
      distinctUntilChanged(),
      debounceTime(400),
      skip(this.skipInitLoading ? 1 : 0),
      takeUntil(this.onDestroy$),
      map(d => d as string)).subscribe(
      d => {
        this.loadData(d)
      }
    )
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: (v: unknown) => void): void {
    this.control?.valueChanges.pipe(distinctUntilChanged()).subscribe(v => fn(v))
  }

  writeValue(obj: null | undefined | string | string[] | ListItem | ListItem[]): void {

    if (this.control && this.control.value != obj) {
      this.control?.setValue(obj, {emitEvent: true});
    }


  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }

  public reload() {
    this.loadData('')
  }

  /**
   * aktualisiert die selectedItems für den multiselect
   */
  public updateSelectedItemsForControlChange(currentlySelected: null | undefined | string | string[] | ListItem | ListItem[] | Model | Model[]): void {
    this.cdr.markForCheck();

    if (!currentlySelected) {
      // aktuell nichts ausgewählt
      this.selectedItems = [];
      this.cdr.detectChanges()
      return
    } else if (typeof currentlySelected === 'string') {
      // eine id ausgewählt
      let selectedItem = this.allSelectOptions.find(item => item.id === currentlySelected);
      if (this.preloadedSelectedModels && !Array.isArray(this.preloadedSelectedModels) && this.preloadedSelectedModels.id === currentlySelected) {
        selectedItem = this.service.modelToMultiSelectItem(this.preloadedSelectedModels);
      }
      this.selectedItems = selectedItem ? [selectedItem] : [];
      this.cdr.markForCheck();
      return;
    } else if (isArray(currentlySelected) && currentlySelected.length > 0 && typeof currentlySelected[0] === 'string') {
      // multiple ids
      let selectedItems = this.allSelectOptions.filter(item => (currentlySelected as string[]).includes(item.id.toString()));
      if (this.preloadedSelectedModels && Array.isArray(this.preloadedSelectedModels)) {
        const filteredList = this.preloadedSelectedModels.filter(item => (currentlySelected as string[]).includes(item.id.toString()))
        if (filteredList.length > 0) selectedItems = filteredList.map(model => this.service.modelToMultiSelectItem(model));
      }
      this.selectedItems = selectedItems;
      return;
    } else if (typeof currentlySelected === 'object' && 'isDisabled' in currentlySelected) {
      // single ListItem
      this.selectedItems = [currentlySelected as ListItem];
      this.cdr.markForCheck();
      return;
    } else if (typeof currentlySelected === 'object' && 'id' in currentlySelected) {
      // single Model
      this.selectedItems = [this.service.modelToMultiSelectItem(currentlySelected as Model)];
      this.cdr.markForCheck();
      return;
    } else if (isArray(currentlySelected) && currentlySelected.length > 0 && typeof currentlySelected[0] === 'object' && 'isDisabled' in currentlySelected) {
      // multiple ListItems
      this.selectedItems = currentlySelected as ListItem[];
      this.cdr.markForCheck();
      return;
    } else if (isArray(currentlySelected) && currentlySelected.length > 0 && typeof currentlySelected[0] === 'object' && 'id' in currentlySelected) {
      // multiple Models
      this.selectedItems = (currentlySelected as Model[]).map(model => this.service.modelToMultiSelectItem(model));
      this.cdr.markForCheck();
      return;
    }

    if (currentlySelected?.length === 0) {
      this.multiSelectIsLoading = false;
      this.selectedItems = [];
      this.cdr.markForCheck();
      return;
    }
  }

  public loadData(query: string) {
    of(query).pipe(
      switchMap((query: string) => {
        this.multiSelectIsLoading = true;
        const params: {
          [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
        } = {};
        if (this.searchOnField) {
          params['filter[' + this.searchOnField + ']'] = query;
        }

        if (!this.backendSort) {
          params['sort'] = this.sort;
        }
        if (this.filters) {
          const converted = this.convertFilters(this.filters)
          converted.forEach(p => {
            params[p.key] = p.value
          })
        }
        return this.service.getPage(1, params, this.disableLocalCache).pipe(map(d => d.data));

      }),
      map((list) => {
        return list.map((model) => this.service.modelToMultiSelectItem(model))
      }),
    )
      .subscribe(
        {
          next: (allModels) => {
            this.multiSelectIsLoading = false
            this.allSelectOptions = sortListByGivenKey<Model>(allModels, this.service.multiSelectSortBy);
            this.updateSelectedItemsForControlChange(this.control.getRawValue())
            this.cdr.detectChanges();
          },
          error: () => {
            console.error('error when loading data from service');
            this.multiSelectIsLoading = false;
            this.control?.setErrors({
              serverError: 'Invalid Data received from Server!'
            }, {emitEvent: true})
            this.control?.markAsDirty()
            this.control?.markAsTouched()
            this.cdr.markForCheck()
          }
        }
      )
  }

  private convertFilters(filters: {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }): { key: string, value: string }[] {
    const result: { key: string, value: string }[] = [];
    for (const key in filters) {
      if (filters[key]) {
        const value = filters[key];
        if (Array.isArray(value)) {
          for (const element of value) {
            result.push({key: 'filter[' + key + ']', value: element});
          }
        } else {
          result.push({key: 'filter[' + key + ']', value: value.toString()});
        }
      }
    }
    return result;
  }
}
